import { motion } from 'framer-motion';
import {
  Package, Airplane, ChartBar, Globe, ShieldCheck,
  ArrowRight, Star, Users, Barcode, FileText,
  CreditCard, Lightning, MagnifyingGlass
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import AnimatedDemo from '../components/AnimatedDemo';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { usePublicT } from '../i18n/usePublicT';

export default function FeaturesPage() {
  const pt = usePublicT();

  const FEATURES = [
    { icon: Package, titleKey: 'feat.packageTracking', descKey: 'feat.packageTrackingDesc', color: '#FF9900' },
    { icon: Users, titleKey: 'featCard.clientMgmt', descKey: 'featCard.clientMgmtDesc', color: '#003166' },
    { icon: FileText, titleKey: 'featCard.smartInvoicing', descKey: 'featCard.smartInvoicingDesc', color: '#10B981' },
    { icon: CreditCard, titleKey: 'feat.stripePaypal', descKey: 'feat.stripePaypalDesc', color: '#0080FF' },
    { icon: Barcode, titleKey: 'featCard.scanner', descKey: 'featCard.scannerDesc', color: '#003166' },
    { icon: ChartBar, titleKey: 'feat.reportsAnalytics', descKey: 'feat.reportsAnalyticsDesc', color: '#8B5CF6' },
    { icon: Globe, titleKey: 'feat.trilingual', descKey: 'feat.trilingualDesc', color: '#0080FF' },
    { icon: ShieldCheck, titleKey: 'featCard.multiTenant', descKey: 'featCard.multiTenantDesc', color: '#003166' },
    { icon: Star, titleKey: 'featCard.customDomains', descKey: 'featCard.customDomainsDesc', color: '#FF9900' },
    { icon: Lightning, titleKey: 'feat.pushNotifications', descKey: 'feat.pushNotificationsDesc', color: '#F43F5E' },
    { icon: Airplane, titleKey: 'featCard.autoInvoice', descKey: 'featCard.autoInvoiceDesc', color: '#10B981' },
    { icon: MagnifyingGlass, titleKey: 'featCard.universalTracker', descKey: 'featCard.universalTrackerDesc', color: '#FF9900' },
  ];

  const HOW_IT_WORKS = [
    { step: '01', titleKey: 'featHow.clientSubmits', descKey: 'featHow.clientSubmitsDesc', color: '#FF9900' },
    { step: '02', titleKey: 'featHow.youProcess', descKey: 'featHow.youProcessDesc', color: '#0080FF' },
    { step: '03', titleKey: 'featHow.autoNotify', descKey: 'featHow.autoNotifyDesc', color: '#10B981' },
    { step: '04', titleKey: 'featHow.getPaid', descKey: 'featHow.getPaidDesc', color: '#8B5CF6' },
  ];

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <SEOHead
        title="Features — Courier CRM, Tracking, Invoicing & Payments"
        description="Package tracking across 1,200+ carriers, client portals, auto-invoicing, Stripe & PayPal payments, push notifications, trilingual support."
        path="/features"
        keywords="courier CRM features, package tracking software, shipping invoice software, client portal courier"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'Track My Package', applicationCategory: 'BusinessApplication', operatingSystem: 'Web', offers: { '@type': 'Offer', price: '99', priceCurrency: 'USD' } }}
      />

      <PublicNav activePage="/features" />

      {/* Hero */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
            {pt('featPage.heroTitle1')}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9900] to-[#FFB060]">{pt('featPage.heroTitle2')}</span>
          </h1>
          <p className="text-gray-400 mt-4 text-base sm:text-lg max-w-2xl mx-auto">{pt('featPage.heroSubtitle')}</p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">{pt('featPage.platformFeatures')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div key={i} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-all"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                data-testid={`feature-card-${i}`}>
                <f.icon size={28} style={{ color: f.color }} weight="duotone" className="mb-3" />
                <h3 className="font-bold text-white text-base mb-1">{pt(f.titleKey)}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{pt(f.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6 bg-[#060B15]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">{pt('how.title')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_IT_WORKS.map((step, i) => (
              <motion.div key={i} className="text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="text-3xl font-bold mb-2" style={{ color: step.color, fontFamily: 'Bebas Neue' }}>{step.step}</div>
                <h3 className="font-bold text-white text-base mb-1">{pt(step.titleKey)}</h3>
                <p className="text-sm text-gray-400">{pt(step.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Animated Demo */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-8">{pt('demo.title')}</h2>
          <p className="text-gray-400 text-center mb-10">{pt('demo.subtitle')}</p>
          <AnimatedDemo />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#060B15]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{pt('cta.readyGetStarted')}</h2>
          <p className="text-gray-400 mb-6">{pt('cta.scheduleSetup')}</p>
          <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')} className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white text-base px-8 py-5 rounded-xl">
            {pt('nav.scheduleCall')} <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
